import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { getErrorMessage } from '../../utils/errorHandler';
import paymentsAPI, { MyPayments } from './paymentsAPI';
import { Cuota } from '../cuentacorriente/ctaCorrienteAPI';
import { ReducedImponible } from '../expirations/expirationsAPI';

interface CurrentPayment {
  selectedCuotas: Cuota[];
  selectedResource: string;
  imponible: ReducedImponible;
}

interface PaymentsState {
  status: 'loading' | 'idle' | 'error' | 'success';
  error?: string;
  decidir?: string[];
  myPayments?: MyPayments[];
  current?: CurrentPayment;
  epagos?: string[];
}

export const initialState: PaymentsState = {
  status: 'idle',
};

export const getDecidirPayments = createAsyncThunk<
  string[],
  void,
  { rejectValue: string }
>('pagos/metodos-decidir', async (_, thunkAPI) => {
  try {
    const response = await paymentsAPI.getDecidirPayments();
    
    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

/////E-Pagos/////
export const getEpagosPayments = createAsyncThunk<
  string[],
  void,
  { rejectValue: string }
>('pagos/metodos-epagos', async (_, thunkAPI) => {
  try {
    
    const response = await paymentsAPI.getEpagosPayments();
    

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getMyPayments = createAsyncThunk<
  MyPayments[],
  void,
  { rejectValue: string }
>('mis-pagos', async (_, thunkAPI) => {
  try {
    const response = await paymentsAPI.getMyPayments();

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    const filteredPayments = response.data.filter(
      payment => payment.status === 'Completed' || payment.status === 'Awaiting Payment'
    );

    return filteredPayments;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const PaymentsState = createSlice({
  name: 'PaymentsState',
  initialState,
  reducers: {
    setCurrent(state, action: PayloadAction<CurrentPayment>) {
      state.current = action.payload;
    },
    cleanCurrent(state) {
      state.current = undefined;
    },
    cleanPaymentState(state) {
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDecidirPayments.pending, state => {
        state.status = 'loading';
      })
      .addCase(getDecidirPayments.fulfilled, (state, action) => {
        state.status = 'idle';
        state.decidir = action.payload;
      })
      .addCase(getDecidirPayments.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message;
      });

    builder
      .addCase(getEpagosPayments.pending, state => {
        state.status = 'loading';
      })
      .addCase(getEpagosPayments.fulfilled, (state, action) => {
        state.status = 'idle';
        state.epagos = action.payload;
      })
      .addCase(getEpagosPayments.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message;
      });

    builder
      .addCase(getMyPayments.pending, state => {
        state.status = 'loading';
      })
      .addCase(getMyPayments.fulfilled, (state, action) => {
        state.status = 'idle';
        state.myPayments = action.payload;
      })
      .addCase(getMyPayments.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message;
      });
  },
});





export const decidirPayments = (state: RootState) => state.payments.decidir ?? [];
export const myPayments = (state: RootState) => state.payments.myPayments ?? [];
export const paymentsStatus = (state: RootState) => state.payments.status;
export const currentPayment = (state: RootState) => state.payments.current;
/////E-pagos////
export const epagosPayments = (state: RootState) => state.payments.epagos ?? [];

export const { cleanPaymentState, setCurrent, cleanCurrent } = PaymentsState.actions;

export default PaymentsState.reducer;
